/**
 * Otokoç Widget Kit — public API.
 *
 * `mountOtokocKit(config)` initialises the full Otokoç-branded chat widget
 * (text + voice) and returns a controller for programmatic interaction.
 *
 * This module is the single entry point consumed by:
 *   - testchat `ChatEmbed.tsx` (ES import)
 *   - production IIFE bundle via `bundle.ts` → `window.OtokocKit.mount()`
 */

import { resetChatConversation } from '../../chatfe/chat/conversation-lifecycle.js';

import { setBookingOtpApiBase } from './bookingOtp.js';
import { mountBookingLocationPicker } from './bookingLocationPicker.js';
import {
  mountBookingComposerAutoFocus,
  mountBookingComposerPlaceholderSync,
  mountBookingLocationSelection,
  mountBookingReviewActions,
} from './bookingEnhancements.js';
import { mountBookingOtpWidgetFlow } from './bookingOtpWidgetFlow.js';
import {
  emitOtpFunnelClientEvent,
  setOtpFunnelTelemetryApiBase,
  type OtpFunnelClientReason,
} from './otpFunnelTelemetry.js';
import {
  LEGACY_OPENING_SKU,
  OTOKOC_LAUNCHER_AVATAR_URL,
  OTOKOC_THEME,
  OTOKOC_CHAT_I18N,
} from './config.js';
import { createBestEffortBrowserUuid, mountChatDeviceMetadata } from './deviceMetadata.js';
import { GengageChat } from './gengageChat.js';
import {
  detectOtokocPageContext,
  exposeOtokocPageContext,
  type OtokocPageContext,
} from './pageContext.js';
import { queryAllAcrossRoots } from './domUtils.js';
import { createOtokocRenderer } from './productShowcase.js';
import { mountSmartScrollOverlay } from './scrollOverlay.js';
import { mountVoiceLauncher } from './voiceLauncher.js';
import { mountConversationTextFormatting } from './conversationTextFormatting.js';
import { mountWidgetOverrides } from './widgetOverrides.js';
import { mountComposerPendingGuard } from './composerPendingGuard.js';

/* ------------------------------------------------------------------ */
/*  Public types                                                      */
/* ------------------------------------------------------------------ */

export type OtokocKitConfig = {
  middlewareUrl: string;
  accountId: string;
  kvkkApproved?: boolean;
  enableLegacyOpeningSeed?: boolean;
  pageContext?: OtokocPageContext;
  onOpenChat?: (initialMessage?: string) => void;
};

export type OtokocKitController = {
  destroy(): void;
  openChat(opts?: {
    state?: 'half' | 'full';
    initialMessage?: string;
    seedContext?: boolean;
    resetHistory?: boolean;
  }): void;
  readonly chat: unknown;
};

const KVKK_LOCAL_STORAGE_KEY = 'kvkkApproved';
export type { OtokocPageContext };

type GengagePageContext = NonNullable<Parameters<GengageChat['init']>[0]['pageContext']>;

type OpeningSeedOptions = NonNullable<Parameters<GengageChat['sendAction']>[1]> & {
  isPdpPrime?: boolean;
  isContextPrime?: boolean;
  preservePills?: boolean;
};

type OpeningSeedChat = GengageChat & {
  sendAction(action: Parameters<GengageChat['sendAction']>[0], options?: OpeningSeedOptions): void;
};

type OpeningSeedAction = Parameters<GengageChat['sendAction']>[0];

const HOST_CHAT_OPEN_CLASS = 'glov-otokoc-chat-open';

type InlineStyleSnapshot = {
  overflow: string;
  overscrollBehavior: string;
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
};

type HostScrollLockState = {
  scrollY: number;
  html: Pick<InlineStyleSnapshot, 'overflow' | 'overscrollBehavior'>;
  body: InlineStyleSnapshot;
};

type HostChatOpenController = {
  prepareForOpen(): void;
  destroy(): void;
};

type ChatLifecycleTelemetryController = {
  onClose(): void;
  destroy(): void;
};

type ConsoleMethodName = 'debug' | 'info' | 'log';

type GengageRuntimeInfoWindow = Window & {
  gengage?: {
    __internal?: {
      sdkRuntimeInfoLogged?: boolean;
    };
  };
};

function silenceGengageRuntimeInfoLog(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const runtimeWindow = window as GengageRuntimeInfoWindow;
  runtimeWindow.gengage ??= {};
  runtimeWindow.gengage.__internal ??= {};
  runtimeWindow.gengage.__internal.sdkRuntimeInfoLogged = true;
}

const CONSOLE_NOISE_PREFIXES = [
  '[voice booking]',
  '[booking-sm][otp-ui]',
  '[gengage] embedded chat',
  '[gengage:',
];

function shouldSuppressConsoleNoise(args: unknown[]): boolean {
  const firstArg = args[0];
  return (
    typeof firstArg === 'string' &&
    CONSOLE_NOISE_PREFIXES.some((prefix) => firstArg.startsWith(prefix))
  );
}

function installConsoleNoiseFilter(): () => void {
  if (typeof console === 'undefined') {
    return () => {};
  }

  const installed: Array<{
    methodName: ConsoleMethodName;
    original: (...args: unknown[]) => void;
    wrapped: (...args: unknown[]) => void;
  }> = [];

  for (const methodName of ['debug', 'info', 'log'] as const) {
    const original = console[methodName];
    if (typeof original !== 'function') {
      continue;
    }

    const boundOriginal = original.bind(console) as (...args: unknown[]) => void;
    const wrapped = (...args: unknown[]) => {
      if (shouldSuppressConsoleNoise(args)) {
        return;
      }
      boundOriginal(...args);
    };

    installed.push({ methodName, original, wrapped });
    console[methodName] = wrapped as Console[typeof methodName];
  }

  return () => {
    for (const { methodName, original, wrapped } of installed) {
      if (console[methodName] === wrapped) {
        console[methodName] = original as Console[typeof methodName];
      }
    }
  };
}

function shouldUseMobileHostLock(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.innerWidth <= 768 || window.matchMedia?.('(pointer: coarse)').matches === true;
}

function mountHostChatOpenClass(): HostChatOpenController {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {
      prepareForOpen: () => {},
      destroy: () => {},
    };
  }

  let scrollLockState: HostScrollLockState | null = null;
  let preparedScrollLockState: HostScrollLockState | null = null;
  let pendingUnlockState: HostScrollLockState | null = null;
  let replayAnimationFrameId: number | null = null;
  const replayTimeoutIds: number[] = [];

  const clearPendingUnlockReplays = () => {
    if (replayAnimationFrameId !== null && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(replayAnimationFrameId);
      replayAnimationFrameId = null;
    }

    while (replayTimeoutIds.length > 0) {
      const timeoutId = replayTimeoutIds.pop();
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    }
  };

  const captureHostScrollState = (): HostScrollLockState => {
    const html = document.documentElement;
    const body = document.body;

    return {
      scrollY: window.scrollY,
      html: {
        overflow: html.style.overflow,
        overscrollBehavior: html.style.overscrollBehavior,
      },
      body: {
        overflow: body.style.overflow,
        overscrollBehavior: body.style.overscrollBehavior,
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
      },
    };
  };

  const restoreHostScrollState = (
    state: HostScrollLockState,
    options: { restoreScroll: 'always' | 'ifAtTop' } = { restoreScroll: 'always' }
  ) => {
    const html = document.documentElement;
    const body = document.body;

    html.style.overflow = state.html.overflow;
    html.style.overscrollBehavior = state.html.overscrollBehavior;
    body.style.overflow = state.body.overflow;
    body.style.overscrollBehavior = state.body.overscrollBehavior;
    body.style.position = state.body.position;
    body.style.top = state.body.top;
    body.style.left = state.body.left;
    body.style.right = state.body.right;
    body.style.width = state.body.width;

    if (
      options.restoreScroll === 'always' ||
      (options.restoreScroll === 'ifAtTop' && state.scrollY > 0 && window.scrollY === 0)
    ) {
      window.scrollTo(0, state.scrollY);
    }
  };

  const prepareForOpen = () => {
    clearPendingUnlockReplays();
    if (pendingUnlockState) {
      restoreHostScrollState(pendingUnlockState, { restoreScroll: 'ifAtTop' });
      pendingUnlockState = null;
    }

    preparedScrollLockState =
      shouldUseMobileHostLock() && !scrollLockState ? captureHostScrollState() : null;
  };

  const isChatStillOpen = () =>
    document.documentElement.classList.contains(HOST_CHAT_OPEN_CLASS) ||
    document.body.classList.contains(HOST_CHAT_OPEN_CLASS) ||
    queryAllAcrossRoots(
      '.gengage-chat-root--open, .gengage-chat-drawer:not(.gengage-chat-drawer--hidden)'
    ).length > 0;

  const replayUnlockState = (
    state: HostScrollLockState,
    options: { restoreScroll: 'always' | 'ifAtTop' } = { restoreScroll: 'ifAtTop' }
  ) => {
    if (isChatStillOpen()) {
      return;
    }

    restoreHostScrollState(state, options);
  };

  const scheduleUnlockReplays = (state: HostScrollLockState) => {
    clearPendingUnlockReplays();
    pendingUnlockState = state;

    if (typeof window.requestAnimationFrame === 'function') {
      replayAnimationFrameId = window.requestAnimationFrame(() => {
        replayAnimationFrameId = null;
        if (pendingUnlockState !== state) {
          return;
        }
        replayUnlockState(state);
      });
    }

    for (const delayMs of [120, 360]) {
      const timeoutId = window.setTimeout(() => {
        const idx = replayTimeoutIds.indexOf(timeoutId);
        if (idx >= 0) {
          replayTimeoutIds.splice(idx, 1);
        }
        if (pendingUnlockState !== state) {
          return;
        }
        if (delayMs === 360) {
          restoreHostScrollState(state, { restoreScroll: 'ifAtTop' });
          pendingUnlockState = null;
          return;
        }
        replayUnlockState(state);
      }, delayMs);
      replayTimeoutIds.push(timeoutId);
    }
  };

  const lockHostScroll = () => {
    clearPendingUnlockReplays();
    if (pendingUnlockState) {
      restoreHostScrollState(pendingUnlockState, { restoreScroll: 'ifAtTop' });
      pendingUnlockState = null;
    }

    if (!shouldUseMobileHostLock() || scrollLockState) {
      preparedScrollLockState = null;
      return;
    }

    const html = document.documentElement;
    const body = document.body;
    scrollLockState = preparedScrollLockState ?? captureHostScrollState();
    preparedScrollLockState = null;

    html.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    body.style.position = 'fixed';
    body.style.top = `-${scrollLockState.scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
  };

  const unlockHostScroll = () => {
    if (!scrollLockState) {
      return;
    }

    const state = scrollLockState;
    scrollLockState = null;
    replayUnlockState(state, { restoreScroll: 'always' });
    scheduleUnlockReplays(state);
  };

  const addClass = () => {
    document.documentElement.classList.add(HOST_CHAT_OPEN_CLASS);
    document.body.classList.add(HOST_CHAT_OPEN_CLASS);
    lockHostScroll();
  };

  const removeClass = () => {
    document.documentElement.classList.remove(HOST_CHAT_OPEN_CLASS);
    document.body.classList.remove(HOST_CHAT_OPEN_CLASS);
    unlockHostScroll();
  };

  const onLauncherClickCapture = (event: Event) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (
      path.some(
        (target) =>
          target instanceof HTMLElement &&
          target.matches('[data-gengage-part="chat-launcher-button"]')
      )
    ) {
      prepareForOpen();
    }
  };

  window.addEventListener('gengage:chat:open', addClass);
  window.addEventListener('gengage:chat:close', removeClass);
  window.addEventListener('gengage:chat:destroy', removeClass);
  document.addEventListener('click', onLauncherClickCapture, true);

  return {
    prepareForOpen,
    destroy: () => {
      clearPendingUnlockReplays();
      window.removeEventListener('gengage:chat:open', addClass);
      window.removeEventListener('gengage:chat:close', removeClass);
      window.removeEventListener('gengage:chat:destroy', removeClass);
      document.removeEventListener('click', onLauncherClickCapture, true);
      document.documentElement.classList.remove(HOST_CHAT_OPEN_CLASS);
      document.body.classList.remove(HOST_CHAT_OPEN_CLASS);
      const state = scrollLockState ?? pendingUnlockState ?? preparedScrollLockState;
      scrollLockState = null;
      pendingUnlockState = null;
      preparedScrollLockState = null;
      if (state) {
        restoreHostScrollState(state, { restoreScroll: 'always' });
      }
    },
  };
}

function mountChatLifecycleTelemetry(sessionId: string): ChatLifecycleTelemetryController {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {
      onClose: () => {},
      destroy: () => {},
    };
  }

  let disposed = false;
  let chatOpen = false;
  let abandonEmitted = false;
  let lastCloseReason: OtpFunnelClientReason | null = null;
  let lastCloseReasonAt = 0;

  const rememberCloseReason = (reason: OtpFunnelClientReason) => {
    lastCloseReason = reason;
    lastCloseReasonAt = Date.now();
  };
  const emitLifecycle = (
    outcome: 'closed' | 'abandoned' | 'reset',
    reason: OtpFunnelClientReason
  ) => {
    emitOtpFunnelClientEvent({
      event: 'chat_lifecycle',
      outcome,
      reason,
      source: 'booking',
      threadId: sessionId,
      dedupeKey: `chat_lifecycle:${sessionId}:${outcome}:${reason}:${Date.now()}`,
    });
  };
  const onChatOpen = () => {
    chatOpen = true;
    abandonEmitted = false;
    emitLifecycle(
      navigator.onLine === false ? 'abandoned' : 'reset',
      navigator.onLine === false ? 'offline' : 'none'
    );
  };
  const onChatClose = () => {
    chatOpen = false;
    abandonEmitted = false;
    const recentReason =
      lastCloseReason && Date.now() - lastCloseReasonAt < 2_000 ? lastCloseReason : 'close_button';
    emitLifecycle('closed', recentReason);
    lastCloseReason = null;
  };
  const onClickCapture = (event: Event) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (
      path.some(
        (target) =>
          target instanceof HTMLElement &&
          target.matches('[data-gengage-part="chat-header-close"], .gengage-chat-close')
      )
    ) {
      rememberCloseReason('close_button_confirmed');
    }
  };
  const onVisibilityChange = () => {
    if (chatOpen && document.visibilityState === 'visible') {
      abandonEmitted = false;
      emitLifecycle(
        navigator.onLine === false ? 'abandoned' : 'reset',
        navigator.onLine === false ? 'offline' : 'none'
      );
      return;
    }
    if (!chatOpen || abandonEmitted || document.visibilityState !== 'hidden') return;
    abandonEmitted = true;
    emitLifecycle('abandoned', 'page_hidden');
  };
  const onOffline = () => {
    if (chatOpen) emitLifecycle('abandoned', 'offline');
  };
  const onOnline = () => {
    if (chatOpen) {
      emitLifecycle(
        document.visibilityState === 'hidden' ? 'abandoned' : 'reset',
        document.visibilityState === 'hidden' ? 'page_hidden' : 'none'
      );
    }
  };
  const onPageHide = () => {
    if (!chatOpen) return;
    abandonEmitted = true;
    emitLifecycle('abandoned', 'page_unload');
  };

  window.addEventListener('gengage:chat:open', onChatOpen);
  document.addEventListener('click', onClickCapture, true);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('offline', onOffline);
  window.addEventListener('online', onOnline);

  return {
    onClose: onChatClose,
    destroy() {
      if (disposed) return;
      disposed = true;
      if (chatOpen && !abandonEmitted) {
        emitLifecycle('abandoned', 'widget_destroyed');
      }
      window.removeEventListener('gengage:chat:open', onChatOpen);
      document.removeEventListener('click', onClickCapture, true);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    },
  };
}

/**
 * Compatibility fallback for older opening behavior. The default path uses the SDK's native
 * pageContext priming so PDP/PLP/home openings preserve their detected context.
 */
function mountOpeningLaunchSeed(
  chat: GengageChat | null,
  options: { kvkkApproved?: boolean }
): () => void {
  let openingSeeded = false;
  let retryTimerId: number | undefined;
  let disposed = false;

  const seedOpeningMessage = (attempt = 0) => {
    if (openingSeeded || disposed) {
      return;
    }

    const send = chat as OpeningSeedChat | null | undefined;

    if (typeof send?.sendAction !== 'function') {
      if (attempt < 5) {
        retryTimerId = window.setTimeout(() => seedOpeningMessage(attempt + 1), 80);
      } else {
        console.warn('[otokoc-kit] GengageChat.sendAction was not available for opening seed');
      }
      return;
    }

    openingSeeded = true;
    let includeKvkk = true;
    try {
      if (options.kvkkApproved === true) {
        includeKvkk = false;
      } else {
        includeKvkk = window.localStorage.getItem(KVKK_LOCAL_STORAGE_KEY) !== '1';
      }
    } catch {
      /* ignore */
    }

    send.sendAction(
      {
        title: '',
        type: 'launchSingleProduct',
        payload: { sku: LEGACY_OPENING_SKU, include_kvkk: includeKvkk },
      },
      { silent: true, preservePills: true }
    );
  };

  const onChatOpen: EventListener = () => {
    seedOpeningMessage();
  };

  window.addEventListener('gengage:chat:open', onChatOpen);

  return () => {
    disposed = true;
    if (retryTimerId !== undefined) {
      window.clearTimeout(retryTimerId);
    }
    window.removeEventListener('gengage:chat:open', onChatOpen);
  };
}

function shouldIncludeKvkkInOpening(options: { kvkkApproved?: boolean }): boolean {
  try {
    if (options.kvkkApproved === true) {
      return false;
    }
    return window.localStorage.getItem(KVKK_LOCAL_STORAGE_KEY) !== '1';
  } catch {
    return true;
  }
}

function buildContextOpeningAction(
  context: OtokocPageContext,
  options: { kvkkApproved?: boolean }
): OpeningSeedAction {
  const includeKvkk = shouldIncludeKvkkInOpening(options);

  if (context.pageType === 'pdp' && context.sku) {
    return {
      title: '',
      type: 'launchSingleProduct',
      payload: { sku: context.sku, include_kvkk: includeKvkk },
    };
  }

  if (
    (context.pageType === 'plp' || context.pageType === 'search') &&
    Array.isArray(context.skuList) &&
    context.skuList.length > 0
  ) {
    return {
      title: '',
      type: 'launchProductList',
      payload: { sku_list: context.skuList, include_kvkk: includeKvkk },
    };
  }

  return {
    title: '',
    type: 'launchHomepage',
    payload: { include_kvkk: includeKvkk },
  };
}

/* ------------------------------------------------------------------ */
/*  mountOtokocKit                                                    */
/* ------------------------------------------------------------------ */

function persistTabSessionId(sessionId: string): string {
  window.__gengageSessionId = sessionId;
  try {
    window.sessionStorage.setItem('gengage_session_id', sessionId);
  } catch {
    /* ignore browser storage errors */
  }
  return sessionId;
}

function resolveTabSessionId(): string {
  const existingGlobalSessionId = window.__gengageSessionId?.trim();
  if (existingGlobalSessionId) {
    return persistTabSessionId(existingGlobalSessionId);
  }

  try {
    const existingSessionId = window.sessionStorage.getItem('gengage_session_id')?.trim();
    if (existingSessionId) {
      return persistTabSessionId(existingSessionId);
    }
  } catch {
    /* ignore browser storage errors */
  }

  return persistTabSessionId(createBestEffortBrowserUuid());
}

export async function mountOtokocKit(config: OtokocKitConfig): Promise<OtokocKitController> {
  const {
    middlewareUrl,
    accountId,
    kvkkApproved,
    enableLegacyOpeningSeed = false,
    pageContext,
  } = config;
  const resolvedPageContext = pageContext ?? detectOtokocPageContext();
  exposeOtokocPageContext(resolvedPageContext);

  setBookingOtpApiBase(middlewareUrl);
  setOtpFunnelTelemetryApiBase(middlewareUrl);

  const removeChatDeviceMetadata = mountChatDeviceMetadata(middlewareUrl);
  const removeWidgetOverrides = mountWidgetOverrides();
  const removeConsoleNoiseFilter = installConsoleNoiseFilter();

  const sessionId = resolveTabSessionId();
  try {
    window.sessionStorage.setItem('kvkkApproved', kvkkApproved ? '1' : '0');
  } catch {
    /* ignore browser storage errors */
  }

  const chat = new GengageChat();
  const chatLifecycleTelemetry = mountChatLifecycleTelemetry(sessionId);
  silenceGengageRuntimeInfoLog();
  // The SDK emits metadata synchronously and has no replay mechanism. Install
  // metadata-driven listeners before init so restored/early context cannot be
  // lost while the SDK is creating its drawer and shadow-root targets.
  const removeOtpFlow = mountBookingOtpWidgetFlow(chat);
  const removeLocationPicker = mountBookingLocationPicker(chat);
  try {
    await chat.init({
      accountId,
      middlewareUrl,
      session: { sessionId },
      // The SDK runtime supports skuList/categoryTree homepage context, but the published
      // chat typings currently expose a narrower pageContext surface.
      pageContext: resolvedPageContext as GengagePageContext,
      locale: 'tr',
      theme: { ...OTOKOC_THEME },
      variant: 'floating',
      mobileBreakpoint: 768,
      hideMobileLauncher: false,
      launcherImageUrl: OTOKOC_LAUNCHER_AVATAR_URL,
      headerAvatarUrl: OTOKOC_LAUNCHER_AVATAR_URL,
      productDetailsExtended: true,
      headerTitle: 'Otokoç AI Asistan',
      onClose: chatLifecycleTelemetry.onClose,
      footerAttribution: {
        label: 'OtokoçAI hatalı bilgi verebilir. Önemli konularda lütfen doğrulama yapınız.',
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      renderer: createOtokocRenderer() as any,
      i18n: { ...OTOKOC_CHAT_I18N },
    });
  } catch (error) {
    removeOtpFlow();
    removeLocationPicker();
    chatLifecycleTelemetry.destroy();
    removeChatDeviceMetadata();
    removeWidgetOverrides();
    removeConsoleNoiseFilter();
    throw error;
  }

  const hostChatOpen = mountHostChatOpenClass();

  const removeScrollOverlay = mountSmartScrollOverlay(chat);
  const removePlaceholderSync = mountBookingComposerPlaceholderSync(chat);
  const removeAutoFocus = mountBookingComposerAutoFocus(chat);
  const removeLocationSelection = mountBookingLocationSelection(chat);
  const removeReviewActions = mountBookingReviewActions(chat);
  const removeConversationTextFormatting = mountConversationTextFormatting();
  const removeComposerPendingGuard = mountComposerPendingGuard(chat);
  let voiceChatOpeningSeeded = false;
  function seedContextOpening() {
    if (voiceChatOpeningSeeded) {
      return;
    }

    voiceChatOpeningSeeded = true;
    const send = chat as OpeningSeedChat;
    if (typeof send.sendAction !== 'function') {
      return;
    }

    window.setTimeout(() => {
      send.sendAction(buildContextOpeningAction(resolvedPageContext, { kvkkApproved }), {
        silent: true,
        preservePills: true,
        isContextPrime: true,
      });
    }, 0);
  }

  const openChatFromVoiceLauncher =
    config.onOpenChat ??
    ((initialMessage?: string) => {
      if (initialMessage) {
        resetChatConversation(chat);
      }
      hostChatOpen.prepareForOpen();
      chat.open(initialMessage ? { state: 'full', initialMessage } : { state: 'full' });
      if (!initialMessage) {
        seedContextOpening();
      }
    });

  const destroyVoice = mountVoiceLauncher({
    middlewareUrl,
    onOpenChat: openChatFromVoiceLauncher,
  });

  const removeOpeningLaunchSeed = enableLegacyOpeningSeed
    ? mountOpeningLaunchSeed(chat, { kvkkApproved })
    : () => {};

  let destroyed = false;

  const controller: OtokocKitController = {
    get chat() {
      return chat;
    },

    openChat(opts) {
      if (opts?.resetHistory) {
        resetChatConversation(chat);
      }
      hostChatOpen.prepareForOpen();
      chat.open(opts ?? { state: 'full' });
      if (opts?.seedContext) {
        seedContextOpening();
      }
    },

    destroy() {
      if (destroyed) return;
      destroyed = true;
      removeScrollOverlay();
      removePlaceholderSync();
      removeAutoFocus();
      removeLocationSelection();
      removeReviewActions();
      removeLocationPicker();
      removeConversationTextFormatting();
      removeComposerPendingGuard();
      chatLifecycleTelemetry.destroy();
      removeOtpFlow();
      removeOpeningLaunchSeed();
      hostChatOpen.destroy();
      removeChatDeviceMetadata();
      removeWidgetOverrides();
      removeConsoleNoiseFilter();
      destroyVoice();
      chat.destroy();
    },
  };

  return controller;
}
